import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, Image as ImageIcon, LogOut, Plus, CreditCard } from "lucide-react";
import { adminApi, apiError, money, openFile } from "@/lib/api";
import { REQUEST_STATUS } from "@/data/fieldServices";

const Pill = ({ children, tone = "slate" }) => (
  <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${tone === "gold" ? "bg-gold/20 text-[#0B132B]" : tone === "green" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{children}</span>
);

const FileLinks = ({ files, icon: Icon, testid }) => files?.length ? (
  <div className="flex flex-wrap gap-2">
    {files.map((f) => (
      <button key={f.id} type="button" onClick={() => openFile(f.id).catch(() => toast.error("Could not open file"))} data-testid={`${testid}-${f.id}`}
        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-[#0B132B] hover:border-gold"><Icon className="h-3 w-3 text-gold" /> {f.original_filename}</button>
    ))}
  </div>
) : null;

const RequestCard = ({ r }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`portal-request-${r.request_number}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><span className="font-mono-x text-xs text-slate-400">{r.request_number}</span><p className="font-semibold text-[#0B132B]">{r.service_name}</p></div>
      <Pill tone={r.status === "completed" ? "green" : "gold"}>{REQUEST_STATUS[r.status]}</Pill>
    </div>
    <p className="mt-2 text-sm text-slate-500">{r.address}, {r.city} {r.zip}</p>
    <div className="mt-3 grid gap-1 text-sm text-slate-600 sm:grid-cols-3">
      <span>Scheduled: <b>{r.scheduled_date ? `${r.scheduled_date} ${r.scheduled_time}` : "Not yet"}</b></span>
      <span>Quote: <b>{r.quote_amount ? money(r.quote_amount) : r.estimate?.total != null ? `Est. ${money(r.estimate.total)}` : "Pending"}</b></span>
      <span>Submitted: {r.created_at.slice(0, 10)}</span>
    </div>
    {(r.reports?.length > 0 || r.photos?.length > 0) && (
      <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
        <FileLinks files={r.reports} icon={FileText} testid="portal-report" />
        <FileLinks files={r.photos} icon={ImageIcon} testid="portal-photo" />
      </div>
    )}
    <ol className="mt-4 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
      {r.history.map((h, i) => <li key={i}>{h.at.slice(0, 16).replace("T", " ")} · <b>{REQUEST_STATUS[h.status] || h.status}</b>{h.message && h.message !== REQUEST_STATUS[h.status] ? ` · ${h.message}` : ""}</li>)}
    </ol>
  </div>
);

const InvoiceRow = ({ inv }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/portal/invoices/${inv.invoice_number}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`portal-invoice-${inv.invoice_number}`}>
      <div>
        <p className="font-semibold text-[#0B132B]">{inv.invoice_number} · {inv.service_name}</p>
        <p className="text-xs text-slate-500">{inv.items.map((i) => `${i.description} ${money(i.amount)}`).join(" · ")}{inv.due_date ? ` · Due ${inv.due_date}` : ""}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-display text-xl font-bold text-[#0B132B]">{money(inv.total)}</span>
        {inv.status === "paid" ? <Pill tone="green">Paid</Pill> : (
          <button type="button" onClick={pay} disabled={busy} data-testid={`portal-pay-${inv.invoice_number}`} className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-[#0B132B] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay now
          </button>
        )}
      </div>
    </div>
  );
};

function usePaymentReturn(reload) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("payment") === "cancel") {
      toast("Payment canceled.");
      setParams({});
    }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      const { data } = await adminApi.get(`/payments/status/${sid}`).catch(() => ({ data: {} }));
      if (data.payment_status === "paid") {
        toast.success("Payment received. Thank you!");
        setParams({});
        reload();
      } else if (data.payment_status === "expired" || ++tries > 10) {
        toast.error("We couldn't confirm the payment yet. Refresh in a moment.");
        setParams({});
      } else setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const load = useCallback(() => {
    adminApi.get("/portal/overview").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  usePaymentReturn(load);

  if (data === null) return <Navigate to="/client-login" replace />;
  if (!data) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  const logout = async () => {
    await adminApi.post("/auth/logout");
    navigate("/client-login");
  };
  const upcoming = [...data.requests.filter((r) => r.status === "scheduled"), ...data.bookings.filter((b) => b.status === "confirmed" || b.status === "pending")];

  return (
    <section className="bg-[#F5F3EC] py-12" data-testid="client-portal">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#0B132B]">Welcome, {data.user.name || data.user.email}</h1>
            <p className="text-sm text-slate-500">{data.user.email}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/request-inspection" data-testid="portal-new-request" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-[#0B132B]"><Plus className="h-4 w-4" /> Request a service</Link>
            <button type="button" onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
        <h2 className="mt-10 font-display text-xl font-bold text-[#0B132B]">Upcoming appointments</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="portal-upcoming">
          {upcoming.map((x) => (
            <div key={x.request_number || x.reference} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
              <b>{x.service_name}</b><br />{x.scheduled_date || x.date} {x.scheduled_time || x.time} · {x.address}
              <div className="mt-1"><Pill>{x.request_number ? REQUEST_STATUS[x.status] : x.status === "pending" ? "Pending approval" : "Confirmed"}</Pill></div>
            </div>
          ))}
          {!upcoming.length && <p className="text-sm text-slate-400">No upcoming appointments.</p>}
        </div>
        <h2 className="mt-10 font-display text-xl font-bold text-[#0B132B]">Invoices</h2>
        <div className="mt-3 space-y-3">{data.invoices.map((i) => <InvoiceRow key={i.invoice_number} inv={i} />)}{!data.invoices.length && <p className="text-sm text-slate-400">No invoices yet.</p>}</div>
        <h2 className="mt-10 font-display text-xl font-bold text-[#0B132B]">Service requests & inspection history</h2>
        <div className="mt-3 space-y-3">{data.requests.map((r) => <RequestCard key={r.request_number} r={r} />)}{!data.requests.length && <p className="text-sm text-slate-400">No requests yet.</p>}</div>
      </div>
    </section>
  );
};

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  useEffect(() => {
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    adminApi.post("/auth/google/session", { session_id: sid })
      .then((r) => navigate(r.data.role === "admin" ? "/admin" : "/client-portal", { replace: true }))
      .catch((e) => {
        toast.error(apiError(e));
        navigate("/client-login", { replace: true });
      });
  }, [hash, navigate]);
  return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
};

export default function Portal() {
  const { hash } = useLocation();
  if (hash?.includes("session_id=")) return <GoogleCallback />;
  return <Dashboard />;
}
